import { Icon, Stack, Tooltip } from '@tonic-one/react';
import { DeleteIcon, FileCopyOIcon, ScanOIcon } from '@tonic-one/react-icons';
import { useTranslation } from 'react-i18next';
import {
  OverAllPolicyType,
  PolicyItemInTable,
  PolicyListModalName,
  PolicyListModalState,
} from '../utils/types';
import { usePermissionStore } from '../../../models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import { useContext, useState } from 'react';
import { PolicyApi } from '@Apis';
import { reportPendo } from '../../../utils/setupPendo';
import IconButton from '../../../components/IconButton';
import {
  OverAllPolicyTypeContext,
  PolicyManualScanRunningQueue,
} from '../OverAllPolicyList/context/OverAllPolicyTypeContext';
import { PolicyType4ManualScan, PolicyTypeEnum } from '../utils/constants';

export const PolicyActionCell = ({
  policy,
  setModal,
  setRefreshTableTrigger,
  policyManualScanRunningQueue,
  disabled: disabledProp,
}: {
  policy: PolicyItemInTable;
  setModal: ({ name, policy }: PolicyListModalState) => void;
  setRefreshTableTrigger: (trigger: Date) => void;
  policyManualScanRunningQueue?: PolicyManualScanRunningQueue;
  disabled: boolean;
}) => {
  const overAllPolicyType = useContext(OverAllPolicyTypeContext);
  const { t } = useTranslation();
  const { widthEditablePermission } = usePermissionStore();
  const editPermission = widthEditablePermission(
    overAllPolicyType === OverAllPolicyType.ATP
      ? PermissionNameEnum.atpPolicy
      : PermissionNameEnum.dlpPolicy
  );
  // record the status of
  const [copyStatus, setCopyStatus] = useState(false);

  const PopperProps = {
    usePortal: true,
  };
  const onManualScanClick = () => {
    setModal({
      name: PolicyListModalName.Scan,
      policy,
    });
  };

  const onCopyClick = async () => {
    try {
      setCopyStatus(true);
      await PolicyApi.apiDuplicatePolicy(policy.policyUuid);
      window.showNotification({
        titleMsg: t('policy.toast.copyPolicy.success'),
        severity: 'success',
      });
    } catch (error: any) {
      if (error.data.error.details[0].code === 'POLICY_API;MAX_COUNT_LIMIT') {
        const { target, limit, policyHolder } = error.data.error.details[0];
        const serviceName = t(`common.service.${target}`);
        const policyHolderString = t(`dashboard.sideBar.${policyHolder}`);
        window.showNotification({
          titleMsg: t('errors.policy.service.outOfLimitation', {
            POLICY_HOLDER: policyHolderString,
            SERVICE: serviceName,
            NUMBER: limit,
          }),
          severity: 'error',
        });
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    } finally {
      setRefreshTableTrigger(new Date());
      reportPendo('Policy.Action.Cope', {
        policyName: policy.policyName,
        type: policy.policyType,
        subType: policy.policySubType,
        uuid: policy.policyUuid,
      });
      setCopyStatus(false);
    }
  };

  const onDeleteClick = () => {
    setModal({
      name: PolicyListModalName.Delete,
      policy,
    });
  };

  //if true, can not be delete or copy, just for dlp
  const isBoxShareLinkPolicy =
    policy.policyType === PolicyTypeEnum.Box && policy.policySubType === 1;

  return (
    <Stack direction="row" flexWrap="nowrap" spacing="6px">
      <IconButton
        onClick={onManualScanClick}
        disabled={
          !editPermission ||
          (policyManualScanRunningQueue &&
            policy.policyType in policyManualScanRunningQueue) ||
          !(policy.policyType in PolicyType4ManualScan) ||
          isBoxShareLinkPolicy ||
          disabledProp
          //'没有rule enabled的policy也没法manual scan'
        }
      >
        <Tooltip
          label={
            policyManualScanRunningQueue &&
            policy.policyType in policyManualScanRunningQueue
              ? t('policy.manualScan.running')
              : t('policy.tooltip.manualScan')
          }
          PopperProps={PopperProps}
        >
          <Icon as={ScanOIcon} />
        </Tooltip>
      </IconButton>
      <IconButton
        onClick={onCopyClick}
        disabled={
          !editPermission || isBoxShareLinkPolicy || copyStatus || disabledProp
        }
      >
        <Tooltip label={t('policy.tooltip.copy')} PopperProps={PopperProps}>
          <Icon as={FileCopyOIcon} />
        </Tooltip>
      </IconButton>
      <IconButton
        onClick={onDeleteClick}
        disabled={
          !editPermission ||
          !!policy.policyIsglobal ||
          isBoxShareLinkPolicy ||
          disabledProp
        }
      >
        <Tooltip label={t('policy.tooltip.delete')} PopperProps={PopperProps}>
          <Icon as={DeleteIcon} />
        </Tooltip>
      </IconButton>
    </Stack>
  );
};
